import LineCommentsIcon from 'LuxKit/Icons/line/LineCommentsIcon'
import NavBarListItemIcon from 'components/NavKit/NavBarListItemIcon'
import config from 'constants/config'
import ContactContext from 'contexts/contactContext'
import GeoContext from 'contexts/geoContext'
import ModalContext from 'contexts/ModalContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { useRenderWhatsApp } from 'hooks/useRenderWhatsApp'
import noop from 'lib/function/noop'
import { getSVGImageUrl } from 'lib/image/imageUtils'
import shouldRenderTimely, { getTimelyLink } from 'lib/timely/timely'
import type { MouseEventHandler } from 'react'
import { memo, useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { isSupportAssistantEnabled } from 'selectors/featuresSelectors'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'
import getIsLoyaltyPrioritySupportNumberEnabled from 'loyalty/selectors/getIsLoyaltyPrioritySupportNumberEnabled'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import { isOtpVerified } from 'lib/jwtToken/checkOtpVerification'
import { generateZendeskCallToken } from 'api/zendesk'
import NavBarOtpModal from './NavDeviceVerificationModal'
import { selectLoggedIn } from 'selectors/accountSelectors'
import LineHeadphonesIcon from 'LuxKit/Icons/line/LineHeadphonesIcon'
import LineStoreIcon from 'LuxKit/Icons/line/LineStoreIcon'
import LineGlobeIcon from 'LuxKit/Icons/line/LineGlobeIcon'
import { getAutoVerificationDialContact } from 'selectors/supportContactSelector'
import { rem } from 'polished'
import styled from 'styled-components'
import useInfiniteConciergeDetails from 'loyalty/hooks/useInfiniteConciergeDetails'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'

const ContactListItem = styled(ClickableListItem)`
  padding: ${rem(8)};
`

interface Props {
  onItemClick?: () => void
}

function NavBarContactsItems(props: Props) {
  const {
    onItemClick = noop,
  }: Props = props

  const intl = useIntl()
  const supportAssistantEnabled = useAppSelector(isSupportAssistantEnabled)
  const { currentRegionCode, currentRegionName } = useContext(GeoContext)
  const currentContact = useContext(ContactContext)
  const showModal = useContext(ModalContext)
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const autoVerificationDialContact = useAppSelector(getAutoVerificationDialContact)
  const isInfiniteMember = useAppSelector(state => getLoyaltyIsInfiniteAccountTier(state))
  const conciergeDetails = useInfiniteConciergeDetails()
  const localContact = currentContact.local
  const internationalContact = currentContact.international
  const shouldRenderWhatsApp = useRenderWhatsApp(currentRegionCode)
  const userHasPhone = useAppSelector(state => state.auth.account.phone) !== undefined
  const isAuRegion = currentRegionCode === 'AU'

  const showTimelyPopup = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()
    onItemClick()
    window.open(getTimelyLink(), 'popup', 'width=600,fullscreen=no,location=no,menubar=no,toolbar=no')
  }, [onItemClick])

  // Handler for phone clicks - shows OTP modal for logged-in users without valid OTP verification
  const handlePhoneClick = useCallback((phoneNumber: string, directDial = false): MouseEventHandler => {
    return (event) => {
      event.preventDefault()
      const dialNumber = directDial || !autoVerificationDialContact?.local.number
        ? phoneNumber
        : autoVerificationDialContact.local.number
      const phoneUrl = `tel:${dialNumber}`;

      (async() => {
        // For direct dial numbers, proceed with default behavior (no OTP verification)
        if (directDial) {
          window.location.href = phoneUrl
          onItemClick()
          return
        }

        // If a user is logged in and already has valid OTP verification in JWT, call directly
        // OTP for now only shows for specific pages — see getAutoVerificationDialContact
        if (isLoggedIn && userHasPhone && isAuRegion && autoVerificationDialContact?.local.number) {
          const hasOtpClaim = isOtpVerified({})
          if (hasOtpClaim) {
            const callToken = await generateZendeskCallToken()
            window.location.href = callToken ? `tel:${dialNumber},${callToken}` : phoneUrl
            onItemClick()
            return
          }

          // Show OTP modal for logged-in users without valid OTP verification
          const result = await showModal<'skipped' | 'verified' | 'closed'>(
            <NavBarOtpModal />,
          )

          if (result === 'verified') {
            const callToken = await generateZendeskCallToken()
            window.location.href = callToken ? `tel:${dialNumber},${callToken}` : phoneUrl
          } else {
            // 'skipped' or 'closed' — proceed directly without call token
            window.location.href = phoneUrl
          }
        } else {
          window.location.href = phoneUrl
        }

        onItemClick()
      })().catch(() => {
        // Fallback: if anything in the async flow throws, still initiate the call
        window.location.href = phoneUrl
        onItemClick()
      })
    }
  }, [autoVerificationDialContact, isAuRegion, isLoggedIn, onItemClick, showModal, userHasPhone])

  const isLoyaltyPrioritySupportNumberEnabled = useAppSelector(getIsLoyaltyPrioritySupportNumberEnabled)
  const loyaltyPrioritySupportNumber = useAppSelector((state) => state.geo.contacts.loyalty)
  const [_loyaltyTier, loyaltyTierName] = useCurrentLoyaltyTier()

  const navbarSupportTitle = useMemo(() => {
    if (
      !isInfiniteMember &&
      isLoyaltyPrioritySupportNumberEnabled &&
      localContact.number === loyaltyPrioritySupportNumber?.local.number
    ) {
      return intl.formatMessage(
        {
          id: 'gOfmr3S15Ppe9r5v7Gsg',
          defaultMessage: '{tierName} Tier Priority Support',
          description: 'Loyalty tier priority support title in navigation bar',
        },
        { tierName: loyaltyTierName },
      )
    }
    return intl.formatMessage(
      {
        id: 'bDFjwMPE5j5nw3gXM9US',
        defaultMessage: 'Local Sales & Support',
        description: 'Local sales and support title in the navigation bar',
      },
    )
  }, [intl, isInfiniteMember, isLoyaltyPrioritySupportNumberEnabled, localContact.number, loyaltyPrioritySupportNumber?.local.number, loyaltyTierName])

  const localSalesSupportAriaLabel = useMemo(() => {
    return intl.formatMessage(
      {
        id: 'qJbfdzOo_u7otZshv5Zp',
        defaultMessage: 'Local Sales & Support contact - {regionName}',
        description: 'Accessibility label for local sales and support contact in navigation bar',
      },
      { regionName: currentRegionName },
    )
  }, [intl, currentRegionName])

  const supportAssistantAriaLabel = useMemo(() => {
    return intl.formatMessage(
      {
        id: '2aIn1Q8SZgUSYQagx-k8',
        defaultMessage: 'Luxury Escapes Support Assistant - {regionName}',
        description: 'Accessibility label for support assistant in navigation bar',
      },
      { regionName: currentRegionName },
    )
  }, [intl, currentRegionName])

  const whatsAppAriaLabel = useMemo(() => {
    return intl.formatMessage(
      {
        id: 'euUi2f5XSdE6DfJoHSty',
        defaultMessage: 'WhatsApp - {regionName}',
        description: 'Accessibility label for WhatsApp contact in navigation bar',
      },
      { regionName: currentRegionName },
    )
  }, [intl, currentRegionName])

  const timelyAriaLabel = useMemo(() => {
    return intl.formatMessage(
      {
        id: 'kar8DxRKzi_wT799Qrla',
        defaultMessage: 'Timely - {regionName}',
        description: 'Accessibility label for Timely appointment booking in navigation bar',
      },
      { regionName: currentRegionName },
    )
  }, [intl, currentRegionName])

  const whatsAppAltText = intl.formatMessage({
    id: 'dq2gc7EzaugkLzA0sz1d',
    defaultMessage: 'WhatsApp',
    description: 'Alternative text for the WhatsApp logo image in the customer support contact area',
  })

  const localPhoneClickHandler = useMemo(
    () => handlePhoneClick(localContact.number),
    [handlePhoneClick, localContact.number],
  )

  const conciergePhoneClickHandler = useMemo(
    () => conciergeDetails ? handlePhoneClick(conciergeDetails.phoneDialNumber, true) : undefined,
    [conciergeDetails, handlePhoneClick],
  )

  const internationalPhoneClickHandler = useMemo(
    () => handlePhoneClick(internationalContact.number, true),
    [handlePhoneClick, internationalContact.number],
  )
  const shouldShowLocalContact = !NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode)

  return (<>
    {isInfiniteMember && conciergeDetails && (
      <ContactListItem
        size="medium"
        aria-label={conciergeDetails.title}
        href={conciergeDetails.phoneHref}
        startIcon={<NavBarListItemIcon Icon={LineHeadphonesIcon} />}
        title={conciergeDetails.title}
        onClick={conciergePhoneClickHandler}
        rel="nofollow"
        subtitle={conciergeDetails.phoneNumber}
      />
    )}
    {shouldShowLocalContact && (
      <ContactListItem
        size="medium"
        aria-label={localSalesSupportAriaLabel}
        href={`tel:${localContact.number}`}
        startIcon={<NavBarListItemIcon Icon={LineHeadphonesIcon} />}
        title={navbarSupportTitle}
        onClick={localPhoneClickHandler}
        rel="nofollow"
        subtitle={localContact.humanReadable}
      />
    )}
    {supportAssistantEnabled && <ContactListItem
      size="medium"
      aria-label={supportAssistantAriaLabel}
      to="/support"
      startIcon={<NavBarListItemIcon Icon={LineCommentsIcon} />}
      title={intl.formatMessage(
        {
          id: 'nc0kwWUK-gfdWaa_sg3Q',
          defaultMessage: 'Support assistant',
          description: 'Support assistant button title in the navigation bar in the navigation bar',
        },
      )}
      onClick={onItemClick}
      rel="nofollow"
      subtitle={intl.formatMessage(
        {
          id: '8hQQDAvss-H0qgaHjGUG',
          defaultMessage: 'Chat now',
          description: 'Chat now subtitle in navigation bar in the support area',
        },
      )}
    />}
    {shouldRenderWhatsApp && <ContactListItem
      size="medium"
      title={intl.formatMessage(
        {
          id: '8lpL-8gsl-GQsu8PGO5P',
          defaultMessage: 'WhatsApp',
          description: 'WhatsApp contact title in navigation bar in the support area',
        },
      )}
      aria-label={whatsAppAriaLabel}
      startIcon={<img src={getSVGImageUrl('whatsapp-logo')} width="20" height="20" alt={whatsAppAltText} />}
      onClick={onItemClick}
      href={`https://wa.me/${config.WHATS_APP_NUMBER}`}
      target="_blank"
      rel="noopener"
    />}
    {shouldRenderTimely(currentRegionCode) && <ContactListItem
      size="medium"
      title={intl.formatMessage(
        {
          id: 'nn3NAy8eVZbMaLQD1yAO',
          defaultMessage: 'Store appointment',
          description: 'Store appointment title in navigation bar in the support area',
        },
      )}
      subtitle={intl.formatMessage(
        {
          id: 'PDtqWFYHZY5AUG5Z-KkA',
          defaultMessage: 'Book now',
          description: 'Book now subtitle in navigation bar in the support area',
        },
      )}
      aria-label={timelyAriaLabel}
      startIcon={<NavBarListItemIcon Icon={LineStoreIcon} />}
      onClick={showTimelyPopup}
      href={getTimelyLink()}
      target="popup"
      rel="noopener"
    />}
    <ContactListItem
      size="medium"
      startIcon={<NavBarListItemIcon Icon={LineGlobeIcon} />}
      aria-label={intl.formatMessage(
        {
          id: 'IY1kWooYze5G7rm_-XlX',
          defaultMessage: 'International Support contact',
          description: 'Accessibility label for international support contact in navigation bar',
        },
      )}
      href={`tel:${internationalContact.number}`}
      title={intl.formatMessage(
        {
          id: 'S4ONBA74WkdP__ei74V_',
          defaultMessage: 'International Support',
          description: 'International support title in the navigation bar in the navigation bar',
        },
      )}
      onClick={internationalPhoneClickHandler}
      rel="nofollow"
      subtitle={internationalContact.humanReadable}
    />
  </>)
}

export default memo(NavBarContactsItems)
